import { ref, onUnmounted, computed } from 'vue';
import { Calendar } from '@libs/api-reference';
import { useLiveQueryV2, CODES } from '@libs/use';
import { MaybeRef } from '@vueuse/core';

import * as API from '../requests';
import { transformServerClock } from '../utils/transform/serverClock';
import {
  IRAN_TIME_ZONE_MILLI_SECONDS_OFFSET,
  ORDERS_QUERY_KEYS,
} from '../constants';

export default function useGetServerClock(
  {
    immediate,
  }: {
    immediate?: MaybeRef<boolean>;
  } = { immediate: true }
) {
  let timerId: ReturnType<typeof setInterval> | null = null;

  const time = ref(Date.now());
  const serverDateTime = ref(Date.now());

  const tick = () => {
    time.value += 1000;
    serverDateTime.value += 1000;
  };

  const {
    result,
    isPending: isLoadingServerClock,
    status: serverClockStatus,
    isError: isErrorLoadingServerClock,
  } = useLiveQueryV2<Calendar>({
    fetcher: API.getServerClock,
    key: ORDERS_QUERY_KEYS.time,
    channel: 'global',
    unsubscribe: false,
    immediate,
    refetchOnWindowFocus: true,
    onSuccess(result) {
      if (result && result.data) {
        if (timerId) {
          clearInterval(timerId);
        }

        serverDateTime.value = Date.parse(result.data.date);

        time.value = serverDateTime.value + IRAN_TIME_ZONE_MILLI_SECONDS_OFFSET;

        timerId = setInterval(tick, 1000);
      }
    },
    onUpdate(signal) {
      const actions = {
        [CODES.TseTimeSection]: transformServerClock,
      };

      if (actions[signal.code]) {
        const data = actions[signal.code](signal.data);

        if (result.value?.data?.tse) {
          if (signal.code === CODES.TseTimeSection) {
            Object.assign(result.value.data.tse, data);
          }
        }
      }
    },
  });

  const serverTime = computed(() => {
    return new Intl.DateTimeFormat('fa', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'GMT',
    }).format(new Date(time.value));
  });

  const serverDate = computed(() => {
    return new Intl.DateTimeFormat('fa', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'GMT',
    }).format(new Date(time.value));
  });

  const timeServer = computed(() => {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'GMT',
    }).format(new Date(time.value));
  });

  const serverClock = computed(() => result.value?.data);

  onUnmounted(() => {
    if (timerId) {
      clearInterval(timerId);
    }
  });

  return {
    serverTime,
    serverDate,
    serverDateTime,
    time,
    timeServer,
    isLoadingServerClock,
    isErrorLoadingServerClock,
    serverClock,
    serverClockStatus,
  };
}
